/**
 * Error Handler Service
 * Gestione centralizzata degli errori
 */

import logger from './Logger';
import { Toast } from 'primereact/toast';
import { MutableRefObject } from 'react';
import { captureException, addBreadcrumb } from './SentryConfig';

export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  VALIDATION = 'VALIDATION',
  PERMISSION = 'PERMISSION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

export interface AppError {
  type: ErrorType;
  message: string;
  statusCode?: number;
  details?: any;
  timestamp: Date;
  userFriendlyMessage?: string;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private toastRef: MutableRefObject<Toast | null> | null = null;
  private errorCallbacks: Map<ErrorType, ((error: AppError) => void)[]> = new Map();
  private last401Time: number = 0;
  private readonly LOGOUT_THROTTLE_MS = 2000; // Throttle per logout automatico

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Configura il toast ref per mostrare messaggi
   */
  setToastRef(ref: MutableRefObject<Toast | null>): void {
    this.toastRef = ref;
  }


  /**
   * Gestisce un errore
   */
  handle(error: unknown, context?: string): void {
    const appError = this.parseError(error, context);

    // Log dell'errore
    this.logError(appError);

    // Send to Sentry for tracking
    if (appError.type !== ErrorType.VALIDATION) {
      captureException(error, {
        context,
        errorType: appError.type,
        statusCode: appError.statusCode,
        details: appError.details
      });
    }

    // Add breadcrumb for error tracking
    addBreadcrumb({
      message: `Error in ${context || 'unknown context'}`,
      category: 'error',
      level: 'error',
      data: {
        type: appError.type,
        message: appError.message
      }
    });

    // Mostra messaggio all'utente
    this.showUserMessage(appError);

    // Esegui callbacks registrati
    this.executeCallbacks(appError);

    // Azioni specifiche per tipo di errore (passa anche il context)
    this.handleSpecificError(appError, context);
  }

  /**
   * Gestisce errore asincrono
   */
  async handleAsync(error: unknown, context?: string): Promise<void> {
    this.handle(error, context);
  }

  /**
   * Parse dell'errore in formato standard
   */
  private parseError(error: unknown, context?: string): AppError {
    const timestamp = new Date();

    // Errore già nel formato AppError
    if (this.isAppError(error)) {
      return error;
    }

    // Network error
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        type: ErrorType.NETWORK,
        message: error.message,
        userFriendlyMessage: 'Problemi di connessione. Verifica la tua connessione internet.',
        timestamp,
        details: { context }
      };
    }

    // HTTP error con status code
    if (this.isHttpError(error)) {
      const errorType = this.getErrorTypeFromStatus(error.statusCode);
      return {
        type: errorType,
        message: error.message || 'Errore HTTP',
        statusCode: error.statusCode,
        userFriendlyMessage: this.getUserFriendlyMessage(errorType, error.statusCode, (error as any).details),
        timestamp,
        details: (error as any).details || { context }
      };
    }

    // Error standard JavaScript
    if (error instanceof Error) {
      return {
        type: ErrorType.CLIENT,
        message: error.message,
        userFriendlyMessage: 'Si è verificato un errore. Riprova più tardi.',
        timestamp,
        details: { 
          stack: error.stack,
          context 
        }
      };
    }

    // Errore sconosciuto
    return {
      type: ErrorType.UNKNOWN,
      message: String(error),
      userFriendlyMessage: 'Si è verificato un errore imprevisto.',
      timestamp,
      details: { error, context }
    };
  }

  /**
   * Determina il tipo di errore dal status code
   */
  private getErrorTypeFromStatus(statusCode?: number): ErrorType {
    if (!statusCode) return ErrorType.UNKNOWN;
    
    if (statusCode === 401 || statusCode === 403) {
      return ErrorType.AUTH;
    } else if (statusCode === 400 || statusCode === 422) {
      return ErrorType.VALIDATION;
    } else if (statusCode >= 500) {
      return ErrorType.SERVER;
    } else if (statusCode >= 400) {
      return ErrorType.CLIENT;
    }
    
    return ErrorType.UNKNOWN;
  }

  /**
   * Ottiene messaggio user-friendly basato sul tipo di errore
   */
  private getUserFriendlyMessage(type: ErrorType, statusCode?: number, details?: any): string {
    // DEBUG
    console.log('🔍 getUserFriendlyMessage:', { type, statusCode, details });

    switch (type) {
      case ErrorType.NETWORK:
        return 'Problema di connessione. Verifica la tua rete e riprova.';

      case ErrorType.AUTH:
        if (statusCode === 401) {
          // Se l'errore ha un messaggio specifico dal backend, usalo
          if (details?.message || details?.messaggio || details?.Messaggio) {
            const message = details.message || details.messaggio || details.Messaggio;

            // Se il messaggio contiene "email" o "confermare", mantieni il messaggio originale
            if (message.toLowerCase().includes('email') ||
                message.toLowerCase().includes('confermare')) {
              return message;
            }

            // Se il messaggio contiene "non trovato" o "verifica", mostra il messaggio originale
            // (es. "Codice fiscale non trovato. Verifica...")
            if (message.toLowerCase().includes('non trovato') ||
                message.toLowerCase().includes('verifica di aver inserito')) {
              return message;
            }

            // Se il messaggio contiene "credenziali" è sicuramente un errore di login
            if (message.toLowerCase().includes('credenziali')) {
              return message; // Usa il messaggio del backend che è più specifico
            }

            // Altrimenti usa un messaggio generico
            return message;
          }
          // Se non c'è messaggio specifico, usa il messaggio di sessione scaduta
          return 'Sessione scaduta. Effettua nuovamente il login.';
        }
        return 'Non hai i permessi per questa operazione.';

      case ErrorType.VALIDATION:
        // Se c'è un messaggio specifico dal backend (es. recupero password con CF non trovato), usalo
        if (details?.message || details?.messaggio || details?.Messaggio) {
          return details.message || details.messaggio || details.Messaggio;
        }
        return 'I dati inseriti non sono validi. Controlla e riprova.';

      case ErrorType.SERVER:
        return 'Il server sta avendo problemi. Riprova tra qualche minuto.';

      case ErrorType.PERMISSION:
        return 'Non hai i permessi necessari per questa azione.';

      default:
        return 'Si è verificato un errore. Riprova più tardi.';
    }
  }

  /**
   * Log dell'errore
   */
  private logError(error: AppError): void {
    const logData = {
      type: error.type,
      message: error.message,
      statusCode: error.statusCode,
      timestamp: error.timestamp.toISOString(),
      details: error.details
    };

    if (error.type === ErrorType.SERVER || error.type === ErrorType.UNKNOWN) {
      logger.error('Critical Error:', logData);
    } else {
      logger.warn('Application Error:', logData);
    }

    // In produzione, invia a servizio di monitoring
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(error);
    }
  }

  /**
   * Mostra messaggio all'utente
   */
  private showUserMessage(error: AppError): void {
    if (!this.toastRef?.current) {
      // Fallback se toast non configurato
      console.warn('Toast not configured for ErrorHandler');
      return;
    }

    // I 401 vengono gestiti in handleSpecificError
    // Mostriamo il toast per TUTTI gli errori (inclusi 401), il redirect verrà gestito dopo se necessario
    const severity = this.getSeverity(error.type);
    const summary = this.getSummary(error.type);
    const detail = error.userFriendlyMessage || error.message;

    this.toastRef.current.show({
      severity,
      summary,
      detail,
      life: severity === 'error' ? 5000 : 3000
    });
  }

  /**
   * Determina severity per toast
   */
  private getSeverity(type: ErrorType): 'success' | 'info' | 'warn' | 'error' {
    switch (type) {
      case ErrorType.VALIDATION:
        return 'warn';
      case ErrorType.NETWORK:
      case ErrorType.AUTH:
      case ErrorType.SERVER:
        return 'error';
      default:
        return 'error';
    }
  }

  /**
   * Determina summary per toast
   */
  private getSummary(type: ErrorType): string {
    switch (type) {
      case ErrorType.NETWORK:
        return 'Errore di Rete';
      case ErrorType.AUTH:
        return 'Errore di Autenticazione';
      case ErrorType.VALIDATION:
        return 'Dati Non Validi';
      case ErrorType.SERVER:
        return 'Errore Server';
      case ErrorType.PERMISSION:
        return 'Permessi Insufficienti';
      default:
        return 'Errore';
    }
  }

  /**
   * Gestisce errori specifici
   */
  private handleSpecificError(error: AppError, context?: string): void {
    switch (error.type) {
      case ErrorType.AUTH:
        if (error.statusCode === 401) {
          // Se il context è "Login", NON fare redirect - è un errore di credenziali errate
          if (context === 'Login') {
            console.log('🔑 Errore 401 durante login - NON fare redirect (credenziali errate)');
            // Il toast è già stato mostrato in showUserMessage
            return;
          }

          // Altrimenti è una sessione scaduta - fai redirect
          // Throttle semplice: ignora 401 multipli entro 2 secondi
          const now = Date.now();
          if (now - this.last401Time < this.LOGOUT_THROTTLE_MS) {
            console.log('🔕 401 throttled - già gestito di recente');
            return;
          }
          this.last401Time = now;

          console.log('⚠️ Errore 401 (sessione scaduta) - redirect al login');

          // Pulisci tutto
          localStorage.clear();
          sessionStorage.clear();

          // Redirect immediato
          if (typeof window !== 'undefined') {
            window.location.replace('/');
          }
        }
        break;

      case ErrorType.NETWORK:
        // Potrebbe mostrare un banner di offline
        this.showOfflineBanner();
        break;

      case ErrorType.SERVER:
        if (error.statusCode === 503) {
          // Servizio in manutenzione
          this.showMaintenanceBanner();
        }
        break;
    }
  }

  /**
   * Registra callback per tipo di errore
   */
  onError(type: ErrorType, callback: (error: AppError) => void): void {
    if (!this.errorCallbacks.has(type)) {
      this.errorCallbacks.set(type, []);
    }
    this.errorCallbacks.get(type)?.push(callback);
  }

  /**
   * Esegue callbacks registrati
   */
  private executeCallbacks(error: AppError): void {
    const callbacks = this.errorCallbacks.get(error.type) || [];
    callbacks.forEach(callback => {
      try {
        callback(error);
      } catch (err) {
        logger.error('Error in error callback:', err);
      }
    });
  }

  /**
   * Invia errore a servizio di monitoring (es. Sentry)
   */
  private sendToMonitoring(error: AppError): void {
    // TODO: Implementare invio a Sentry o altro servizio
    // if (window.Sentry) {
    //   window.Sentry.captureException(error);
    // }
  }


  /**
   * Mostra banner offline
   */
  private showOfflineBanner(): void {
    // TODO: Implementare banner offline
    logger.info('User appears to be offline');
  }

  /**
   * Mostra banner manutenzione
   */
  private showMaintenanceBanner(): void {
    // TODO: Implementare banner manutenzione
    logger.info('Service is under maintenance');
  }

  /**
   * Type guards
   */
  private isAppError(error: any): error is AppError {
    return error && 
           typeof error === 'object' && 
           'type' in error && 
           'message' in error &&
           'timestamp' in error;
  }

  private isHttpError(error: any): error is { statusCode: number; message?: string } {
    return error && 
           typeof error === 'object' && 
           'statusCode' in error &&
           typeof error.statusCode === 'number';
  }

  /**
   * Crea un errore custom
   */
  createError(
    type: ErrorType,
    message: string,
    statusCode?: number,
    details?: any
  ): AppError {
    return {
      type,
      message,
      statusCode,
      details,
      timestamp: new Date(),
      userFriendlyMessage: this.getUserFriendlyMessage(type, statusCode, details)
    };
  }
}

export default ErrorHandler.getInstance();